import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNotifications } from "../../hooks/useNotifications";
import { NotificationDropdown } from "./notification-dropdown";

export function NotificationBell() {
  const {
    notifications,
    unreadCount,
    loading,
    markRead,
    dismiss,
    markAllRead,
    dismissAll,
    removeLocal,
  } =
    useNotifications();

  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
      >
        <Bell className="h-4 w-4 text-slate-600 dark:text-slate-300" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          // D4 (mobile a11y hunt): on a 393px viewport the `right-0 w-80` (320px)
          // panel, anchored to the right-aligned bell, extended ~13px past the
          // LEFT viewport edge (box x:-13 → right:307), clipping the
          // "Notifications (N unread)" heading. Below `sm` the panel now pins to
          // the viewport edges (`fixed inset-x-2`, auto width) so it can never
          // overshoot either side; at `sm` and up it restores the original
          // bell-anchored `absolute right-0 w-80` behaviour (desktop unchanged).
          className="fixed inset-x-2 top-16 z-50 mt-0 w-auto overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 text-sm shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:z-auto sm:mt-2 sm:w-80 dark:border-slate-700 dark:bg-slate-800"
        >
          <NotificationDropdown
            notifications={notifications}
            loading={loading}
            onMarkRead={markRead}
            onDismiss={dismiss}
            onMarkAllRead={markAllRead}
            onDismissAll={dismissAll}
            onRemoveLocal={removeLocal}
          />
        </div>
      )}
    </div>
  );
}
